import { useQuery } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Enums } from "@how/database";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export type Channel = Enums<"communication_channel">;

/** The channels worth reporting on. `mail` needs a postal address, which is a different question. */
export const REPORTED_CHANNELS: readonly Channel[] = ["sms", "email", "phone"];

export const CHANNEL_LABELS: Record<Channel, string> = {
  sms: "Text message",
  email: "Email",
  phone: "Phone call",
  mail: "Post",
};

/**
 * Three counts, kept apart on purpose.
 *
 * They are three different problems for a church and only two of them can be fixed by asking. Collapsing them into
 * one number hides the one they can actually do something about.
 */
export interface ChannelCoverage {
  readonly channel: Channel;
  /** Agreed, and reachable: a live person with an address for this channel and an opted-in record. */
  readonly reachable: number;
  /** Has an address, but has not agreed — or has said no. These are the people to go and ask. */
  readonly notAgreed: number;
  /** No address of the kind this channel needs. Asking will not help; collecting a number will. */
  readonly noAddress: number;
  readonly total: number;
}

export type ConsentState = Enums<"consent_state">;

/** The states in the order the screen reads them: the number a church can act on first. */
export const CONSENT_STATES: readonly ConsentState[] = ["agreed", "not_agreed", "no_address"];

/** What a state means for one channel, in the words the consent screen uses. */
export function consentStateLabel(channel: Channel, state: ConsentState): string {
  if (state === "agreed") return "Agreed";
  if (state === "not_agreed") return "Not agreed, or asked us not to";
  return `No ${channel === "email" ? "email address" : "phone number"} on file`;
}

/** One number on the consent screen, as a filter on the people list. */
export interface ConsentFilter {
  readonly channel: Channel;
  readonly state: ConsentState;
}

/** The filter as it sits in the address, `channel.state`, so a list can be bookmarked or sent to a volunteer. */
export function consentFilterParam(filter: ConsentFilter): string {
  return `${filter.channel}.${filter.state}`;
}

/** Reads the address back; anything that is not a reported channel and a known state is no filter at all. */
export function parseConsentFilter(value: string): ConsentFilter | null {
  const parts = value.split(".");
  if (parts.length !== 2) return null;
  const [channel, state] = parts;
  const known =
    REPORTED_CHANNELS.find((c) => c === channel) !== undefined &&
    CONSENT_STATES.find((s) => s === state) !== undefined;
  return known ? { channel: channel as Channel, state: state as ConsentState } : null;
}

/**
 * How much of the church a given channel can actually reach.
 *
 * The definition of agreed, not agreed and no address lives in the database (`private.person_channel_consent`), and
 * the people list reads the same definition to show the people behind each number (#112). Counting it here as well
 * would be a second definition that could drift from the list a count opens.
 */
export function useConsentCoverage() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["consent-coverage", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<ChannelCoverage[]> => {
      const { data, error } = await supabase.rpc("consent_coverage", {
        p_tenant_id: tenantId ?? "",
      });
      if (error) throw error;
      const count = (channel: Channel, state: ConsentState): number =>
        data.find((row) => row.channel === channel && row.state === state)?.people ?? 0;
      return REPORTED_CHANNELS.map((channel) => {
        const reachable = count(channel, "agreed");
        const notAgreed = count(channel, "not_agreed");
        const noAddress = count(channel, "no_address");
        return {
          channel,
          reachable,
          notAgreed,
          noAddress,
          total: reachable + notAgreed + noAddress,
        };
      });
    },
  });
}
